import React, { useState, MouseEvent } from 'react'

import { Button, MENU_ITEM_SX, MENU_PAPER_SX } from '@repo/ui'
import { DrawerPopup } from 'components/UI/DrawerPopup'
import { PICKER_DROPDOWN, PICKER_SHEET } from 'components/UI/pickerListStyle'
import { MENU_ROW_LABEL_SX, menuRowToneSx } from 'components/UI/menuRowTone'

import {
  Box,
  Divider,
  Menu as MuiMenu,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import type { PopoverOrigin, SxProps, Theme } from '@mui/material'
import { MoreVertical } from 'lucide-react'

/**
 * A single row in an {@link ActionMenu}. Mirrors the platform `ActionDropdownItem`
 * shape (apps/platform/src/components/input/ActionDropdown) so the two apps describe
 * menus the same way — declaratively, one array, per-item icon.
 */
export type ActionMenuItem = {
  label: string
  onClick: () => void
  /** Leading glyph. Every item should have one (working-conventions §21). */
  icon?: React.ReactNode
  /**
   * A second line under the label — typically why the row is unavailable
   * ("Coming soon", "Gifting is one at a time").
   *
   * The sheet was always meant to carry this: `presentation: 'sheet'` exists
   * because a sheet has room for "an icon, a full label and (later) a line of
   * description", where a dropdown pinned to a 40px kebab does not. This is that
   * line, and it renders in BOTH presentations rather than the sheet only —
   * `tooltip` is the desktop-only affordance, and a reason that only a mouse can
   * read is unreachable in an app whose entry point is tapping a tag with a
   * phone. §46's forcing function ("a disabled item MUST carry a reason") needs a
   * channel that survives touch.
   */
  description?: React.ReactNode
  /** Red text + icon; use for irreversible actions (paired with a confirm). */
  destructive?: boolean
  disabled?: boolean
  /** Shown on hover (desktop) — typically why the item is disabled. */
  tooltip?: React.ReactNode
  /** Render a divider above this item. */
  divider?: boolean
  /** Filtered out when false. */
  visible?: boolean
}

interface ActionMenuProps {
  items: ActionMenuItem[]
  /**
   * Trigger element. Defaults to a kebab (⋮) button. Ignored in controlled mode
   * (when `open` is provided) — the caller owns the trigger there.
   */
  trigger?: React.ReactNode
  /**
   * Controlled open state. When provided, the menu renders no trigger of its own
   * and is driven entirely by the caller (e.g. a header kebab). On mobile it still
   * opens as a bottom drawer, on desktop as a menu anchored to `controlledAnchorEl`.
   */
  open?: boolean
  /** Close handler for controlled mode. */
  onClose?: () => void
  /** Desktop anchor for controlled mode (the header kebab element). */
  controlledAnchorEl?: HTMLElement | null
  /** Mobile bottom-drawer title (hidden header by default). */
  title?: string
  anchorOrigin?: PopoverOrigin
  transformOrigin?: PopoverOrigin
  /** Extra sx for the mobile drawer paper. */
  drawerPaperSx?: SxProps<Theme>
  /**
   * How the list is presented.
   *
   * `'auto'` (default, unchanged): a dropdown anchored to the trigger at md+, a
   * bottom sheet below md.
   *
   * `'sheet'`: the SHEET at every width — i.e. one `DrawerPopup`, which under a
   * `DesktopSheetContext` provider is itself a centred dialog at md+. Use it when
   * the rows are the destination rather than a quick pick: a sheet has room for an
   * icon, a full label and (later) a line of description, where a dropdown anchored
   * to a 40px kebab does not. The certificate page's Manage actions are the case
   * this exists for — the set is expected to grow past Gift / Report.
   *
   * Note the two branches take DIFFERENT row sizing: `'sheet'` reads `PICKER_SHEET`
   * (16px text, 24px icon, 48px rows) at all widths, because that is what the rows
   * ARE here — a sheet's rows, not a dropdown's, even on a desktop where the sheet
   * has become a dialog.
   */
  presentation?: 'auto' | 'sheet'
}

/**
 * Shared app action / overflow menu — the customer-app counterpart of the platform
 * `ActionDropdown`. Renders a house-styled dropdown on desktop and a bottom drawer on
 * mobile from ONE `items[]` array, so call-sites only describe actions (VERA-315).
 */
export const ActionMenu = ({
  items,
  trigger,
  open: controlledOpen,
  onClose,
  controlledAnchorEl = null,
  title = '',
  anchorOrigin = { vertical: 'bottom', horizontal: 'right' },
  transformOrigin = { vertical: 'top', horizontal: 'right' },
  drawerPaperSx,
  presentation = 'auto',
}: ActionMenuProps) => {
  const theme = useTheme()
  /**
   * Two questions that used to be one boolean, and conflating them is what a
   * `presentation='sheet'` desktop render would have got wrong:
   *
   * `isNarrow` — is this a PHONE? Decides hover affordances (a tooltip is dead
   * weight on touch) and nothing else.
   * `asSheet` — is this a SHEET? Decides which tree mounts and how rows are sized.
   *
   * They coincide under `presentation='auto'`, which is why one flag sufficed until
   * a sheet was asked for at desktop width.
   */
  const isNarrow = useMediaQuery(theme.breakpoints.down('md'))
  const asSheet = presentation === 'sheet' || isNarrow

  const isControlled = controlledOpen !== undefined

  const [internalAnchorEl, setInternalAnchorEl] = useState<HTMLElement | null>(null)
  const anchorEl = isControlled ? controlledAnchorEl : internalAnchorEl
  const open = isControlled ? controlledOpen : Boolean(internalAnchorEl)

  const visibleItems = items.filter(item => item.visible !== false)

  const handleOpen = (event: MouseEvent<HTMLElement>) => {
    event.preventDefault()
    event.stopPropagation()
    setInternalAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    if (isControlled) onClose?.()
    else setInternalAnchorEl(null)
  }

  const handleItemClick = (item: ActionMenuItem) => {
    if (item.disabled) return
    handleClose()
    item.onClick()
  }

  const renderRow = (item: ActionMenuItem, index: number) => {
    // This one row renders in BOTH the MuiMenu and the DrawerPopup below
    // (`asSheet` picks which JSX tree mounts it) — so it must pick its sizing
    // per render rather than share one fixed value.
    // Dropdown = PICKER_DROPDOWN (unchanged appearance). Sheet = PICKER_SHEET
    // (16px text, 24px icon, explicit line-height) — these rows are primary
    // phone tap targets (the certificate options sheet is 6 of them), and
    // previously read the SAME dense desktop padding (VERA-291 follow-up).
    // Keyed on the PRESENTATION, not the viewport: a sheet's rows are a sheet's
    // rows at 1280 too, where the sheet has become a centred dialog.
    const sizing = asSheet ? PICKER_SHEET : PICKER_DROPDOWN

    // Icons are opaque nodes supplied by the caller, sized once for both
    // contexts (typically 18px, tuned for the desktop menu). Resize ONLY on
    // the mobile render so desktop icon sizing is untouched — every current
    // icon is a lucide-react glyph, which accepts a `size` prop.
    const icon =
      item.icon && asSheet && React.isValidElement(item.icon)
        ? React.cloneElement(item.icon as React.ReactElement<{ size?: number }>, {
            size: PICKER_SHEET.iconSize,
          })
        : item.icon

    const row = (
      <Box
        key={item.label}
        role="menuitem"
        aria-disabled={item.disabled}
        onClick={() => handleItemClick(item)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          width: '100%',
          // DESKTOP takes the house menu-row chrome (12px on four sides, 6px nested
          // radius, 4px gap between rows) so this dropdown matches every other menu in
          // both apps. MOBILE keeps PICKER_SHEET: that branch renders inside a bottom
          // sheet, which is not a popover menu — its rows are phone tap targets sized
          // against the 44px minimum, and the paper-inset convention does not apply to
          // a full-width sheet.
          //
          // The padding is unchanged either way (PICKER_DROPDOWN.itemPX/PY resolve to
          // 12px on this app's 8px spacing base); what the desktop row gains is the
          // house radius and the inter-row gap.
          /*
            `asSheet`, not `isMobile`. This line survived a MERGE as a reference to
            a variable that no longer exists: `isMobile` was split into `isNarrow`
            (pointer — governs hover affordances) and `asSheet` (presentation —
            governs which tree mounts and how rows are sized), and main
            independently edited this file still using the old name. Git merged
            both texts with ZERO conflicts and produced a ReferenceError that
            blanked the certificate page at runtime.

            The branch is about PRESENTATION — sheet rows take the picker's sizing,
            dropdown rows take the house `MENU_ITEM_SX` — so `asSheet` is the
            correct half of the split.
          */
          ...(asSheet
            ? { px: sizing.itemPX, py: sizing.itemPY, borderRadius: `${sizing.radius}px` }
            : MENU_ITEM_SX),
          /*
            The app's one menu-row tone (`menuRowTone.ts`). This row used to sit at
            `common.black` and change only its BACKGROUND on hover, so a kebab row
            and a Layout-popover row — two menus, one app — answered the pointer
            differently. An action menu has no selected state, so every row is the
            "unselected" case: muted at rest, accent on hover.

            `colorFor` is gone with it; the tone function owns all three of its
            branches (disabled, destructive, default) so there is no second place a
            row's colour is decided.
          */
          ...menuRowToneSx(theme, { destructive: item.destructive, disabled: item.disabled }),
          cursor: item.disabled ? 'not-allowed' : 'pointer',
          /*
            The blanket fade is NOT applied to a row that explains itself.

            `menuRowToneSx` already takes a disabled row to `text.disabled`
            (2.78:1 against the sheet, measured). Multiplying that by 0.6 puts the
            row near 1.7:1 and drags the description down with it, on the one line
            the user has to read to understand why the row is inert. Two signals
            were stacked where one was doing the work.

            A row with no description keeps the fade: there the opacity IS the
            whole signal, and nothing legible is being sacrificed to it.
          */
          opacity: item.disabled && !item.description ? 0.6 : 1,
        }}
      >
        {icon && (
          // `alignSelf: flex-start` only matters once a description makes the row
          // two lines tall: centred against a two-line block the glyph drifts down
          // to sit beside the description, which is not what it labels. A nudge of
          // 1px lands its optical centre on the label's cap height.
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              color: 'inherit',
              ...(item.description ? { alignSelf: 'flex-start', mt: '1px' } : null),
            }}
          >
            {icon}
          </Box>
        )}
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="subtitle1"
            sx={{
              m: 0,
              fontWeight: 400,
              color: 'inherit',
              ...(asSheet && { fontSize: PICKER_SHEET.fontSize, lineHeight: PICKER_SHEET.lineHeight }),
            }}
          >
            {item.label}
          </Typography>
          {item.description && (
            /*
              `text.secondary`, NOT `inherit`, and the resulting inversion is
              deliberate.

              The label inherits the row's tone, which `menuRowToneSx` takes to
              `text.disabled` when the row is inert. Measured in the browser on
              the certificates bulk menu: that composites to #9E9E9E, 2.78:1
              against the sheet, while this line at `text.secondary` (#7A7A7A)
              reads 4.29:1 — so the explanation is DARKER than the thing it
              explains, which looks upside-down for about a second and is then
              obviously right. The label is greyed because it is unavailable; the
              sentence saying why is the only live content on the row and is the
              reason the row is shown at all rather than hidden. Fading it to the
              disabled grey would hide the one thing worth reading, and 2.78:1 is
              not a contrast to put a load-bearing sentence at on a phone.

              (4.29 is itself marginally under the 4.5 AA floor. That is
              `text.secondary` — every piece of helper copy in this app sits on
              it — not something introduced here, and moving it is an app-wide
              sweep rather than a menu change.)

              `body2` is the app's 14px helper floor (the same one the gift
              sheet's helper copy takes), and `lineHeight: 1.4` keeps a wrapped
              two-line reason from reading as a paragraph.
            */
            <Typography
              variant="body2"
              sx={{ color: 'text.secondary', lineHeight: 1.4, mt: '2px' }}
            >
              {item.description}
            </Typography>
          )}
        </Box>
      </Box>
    )

    const content =
      // Hover-only affordance, so it is keyed on POINTER (isNarrow), not on which
      // tree is mounting — a `presentation='sheet'` dialog at 1280 still has a mouse.
      item.tooltip && !isNarrow ? (
        <Tooltip key={item.label} title={item.tooltip}>
          {/* span so the tooltip anchors even when the row is disabled */}
          <span style={{ width: '100%' }}>{row}</span>
        </Tooltip>
      ) : (
        row
      )

    return (
      <React.Fragment key={item.label}>
        {item.divider && index > 0 && <Divider sx={{ my: 0.5 }} />}
        {content}
      </React.Fragment>
    )
  }

  const defaultTrigger = (
    <Button aria-label="Open actions menu" variant="text" onClick={handleOpen} sx={{ px: 2, minWidth: 0 }}>
      <MoreVertical size={20} />
    </Button>
  )

  return (
    <>
      {!isControlled &&
        (trigger ? (
          <Box component="span" onClick={handleOpen} sx={{ display: 'inline-flex', cursor: 'pointer' }}>
            {trigger}
          </Box>
        ) : (
          defaultTrigger
        ))}

      {!asSheet && (
        <MuiMenu
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          anchorOrigin={anchorOrigin}
          transformOrigin={transformOrigin}
          slotProps={{
            paper: {
              sx: {
                // House paper chrome: the 4px inset, the matching list inset, and the
                // viewport-safe max height. Replaces a hand-set `p: 1` — which on this
                // app's 8px spacing base was 8px, i.e. the right TOTAL by accident,
                // carried entirely on the paper with the list at 0.
                // ...as is the 8px trigger gap, which used to be restated below as
                // `mt: 1` — 8px here on the app's MUI-default base, but 4px from the
                // identical line in the platform.
                ...MENU_PAPER_SX,
                minWidth: 200,
                borderRadius: '12px',
                boxShadow: '0px 8px 24px rgba(0,0,0,0.12)',
              },
            },
          }}
        >
          {visibleItems.map((item, index) => renderRow(item, index))}
        </MuiMenu>
      )}

      {asSheet && (
        <DrawerPopup
          title={title}
          hideHeader={!title}
          handleOpen={open}
          handleClose={handleClose}
          paperSx={{
            height: 'auto',
            /*
              The hardcoded 16 is NOT applied on the `sheet` presentation, for two
              reasons. (1) Under `DesktopSheetContext` this Paper is a centred
              DIALOG at md+, which is round on all four corners — setting only the
              TOP two to 16 would leave the bottom two on the theme token (10 on the
              current tenant) and the dialog visibly lopsided. (2) `DrawerPopup`
              already sets both top corners from `theme.shape.borderRadius`, and its
              own docstring says the Paper and the content Box must agree because
              the Paper is what CLIPS the content — this override is precisely the
              disagreement it warns about (paper 16, content 10).

              Left in place for `presentation='auto'` so the four existing sheets
              (CertificatesMenu, the account overflow, SharePage) do not move. That
              they should also drop it is a separate, deliberate change.
            */
            ...(presentation === 'auto'
              ? { borderTopLeftRadius: 16, borderTopRightRadius: 16 }
              : null),
            ...drawerPaperSx,
          }}
          child={
            /*
              NO horizontal padding here. `DrawerPopup` already applies `px: 2` (16px)
              to the content of every bottom sheet (DrawerPopup.tsx), which is exactly
              where the Filters and Sort sheets get their 16px — they add nothing on
              top of it.

              This Stack was adding its own on top, so the kebab sheet's rows sat
              further in than every other sheet: 16 (DrawerPopup) + 8 (here) + 12 (the
              row's own padding) = 36px to the icon, against 16px to a filter field's
              edge. With this at 0 the row's highlight pill starts at 16 — the same
              line the Filters search field starts on — and the label sits 12px inside
              it, which is the pill's internal padding, analogous to a field's.

              Not fixed in `PICKER_SHEET.itemPX`: that token is shared with
              PICKER_DROPDOWN and carries the pill's inner padding, which is correct.
            */
            <Stack spacing={0} sx={{ px: 0, pb: 1 }}>
              {visibleItems.map((item, index) => renderRow(item, index))}
            </Stack>
          }
        />
      )}
    </>
  )
}
